import { saveAs } from 'file-saver'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

// UI
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { toast } from 'react-toastify'

import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'
import DelegationDetailModal from '../components/DelegationDetailModal'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//helper
import { createExcelBlob } from '@/utils/fileHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { RawTextCell } from '@/components/Table/components/Cell'
import DateCell from '@/components/Table/components/Cell/DateCell'
import { useDelegation } from '../useDelegation'
import getDelegationTableColumns from '../constants/delegationTableColumns'
import LoadingSection from '../../../components/Elements/LoadingSection/LoadingSection'

const DelegationListPage = () => {
  //Modal
  const [modalDetail, setModalDetail] = useState({
    show: false,
    isCreate: true,
  })

  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:delegation`, {})
  const query = convertTableStateToQuery(storedTableState)

  //Hooks

  const columnHelper = createColumnHelper()
  const dispatch = useDispatch()
  const { useFetchAllDelegations, useRemoveDelegation, useUpdateDelegation, useUploadDelegations } = useDelegation()

  const uploadDelegations = useUploadDelegations()
  const removeDelegation = useRemoveDelegation()
  const updateDelegation = useUpdateDelegation()
  const { data: delegationResponse, isLoading } = useFetchAllDelegations(query)
  const { data: allDelegationResponse } = useFetchAllDelegations({
    page: 1,
    pageSize: 0,
  })

  const handleOnRowClick = (row) => {
    setModalDetail({
      show: true,
      isCreate: false,
      detail: row?.original,
    })
  }
  const handleAddDelegation = () => {
    setModalDetail({
      show: true,
      isCreate: true,
      detail: {},
    })
  }

  const handleDownload = useCallback(() => {
    if (!allDelegationResponse?.data) {
      return
    }
    try {
      const excelData = allDelegationResponse?.data.map((row) => ({
        delegator: row.delegator,
        delegatee: row.delegatee,
        delegatedCostCenter: row.delegatedCostCenter,
        validStartDate: row.validStartDate,
        validEndDate: row.validEndDate,
        status: row.status,
      }))

      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Delegation.xlsx')
    } catch (err) {
      console.log(err)
    }
  }, [allDelegationResponse?.data])

  const handleDelete = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await removeDelegation.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeDelegation]
  )

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await updateDelegation.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateDelegation]
  )
  const defaultColumns = getDelegationTableColumns({
    handleActivate,
    handleDelete,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <DelegationDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>Delegations</Heading>
          <HStack mx="5">
            <Button colorScheme="gray" onClick={handleDownload}>
              Download
            </Button>
          </HStack>
          <Spacer />
          <Button colorScheme="green" onClick={handleAddDelegation}>
            Add Delegation
          </Button>
        </Flex>
        <Box p={5} overflow="scroll">
          <FullTable
            data={delegationResponse?.data ? delegationResponse.data : []}
            rowCount={delegationResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default DelegationListPage
